import { UnleashClient } from 'unleash-proxy-client'
import Config from '@/config'
import sentry from './sentry'
import { flags } from '@/utils/abtest.utils'
import { saveCachedUnleashContext, getCachedUnleashContext } from '@/vuex/modules/loading-page/loading-page.utils'
import { UNLEASH_SESSION_COOKIE } from './unleashSession.constants'

// Light-weight read of the shared anchor cookie for the initial context.
// main.ts authoritatively re-sets it via setSessionId() right after mint.
// Mirrors readRawSessionCookie in unleashSession.js (raw key, decodeURIComponent
// URIError → raw fallback); the cookie NAME has one source of truth via the
// imported UNLEASH_SESSION_COOKIE constant so the two readers can't drift.
export const readSessionCookie = () => {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(new RegExp(`(?:^|; )${UNLEASH_SESSION_COOKIE}=([^;]*)`))
  if (!match) return undefined
  // decodeURIComponent throws URIError on a malformed cookie; fall back to the
  // raw value so a bad cookie can never crash bootstrap.
  try {
    return decodeURIComponent(match[1])
  } catch (error) {
    return match[1]
  }
}

class UnleashService {
  constructor(additionalParams = {}) {
    try {
      this.unleashClient = new UnleashClient({
        url: Config.unleashEndpoint,
        clientKey: Config.unleashClientKey,
        appName: 'presenter',
        createAbortController: () => null,
        // This is the on-update function for real-time updates from Unleash to continuously refresh feature flags from the Unleash server.
        // Developers should carefully consider which features to use with this function, as it needs to handle cases where a feature might be toggled on or off while users are actively using it.
        // Additionally, there may be unforeseen cases to account for.
        refreshInterval: 0,
        context: {
          sessionId:
            readSessionCookie()
            || localStorage.getItem('unleash:repository:sessionId')?.replace(/^"|"$/g, '')
            || undefined,
        },
        ...additionalParams,
      })
      this.ready = new Promise((resolve) => this.unleashClient.on('ready', resolve))
    } catch (error) {
      sentry.captureException(error)
      this.unleashClient = null
      // in case of error, don’t leave `ready` hanging
      this.ready = Promise.resolve()
    }
  }

  // Ideally, Only 2 requests to Unleash proxy should be made
  // 1 for the anonymous (with sessionId is the stickiness)
  // 2 for the user context (with userId is the stickiness)
  start() {
    if (flags.canUseNewUnleashMechanism) {
      this.startV2()
    } else {
      this.startV1()
    }
  }

  startV1() {
    this.unleashClient.start()
  }

  startV2() {
    // only start once!
    // can create a v2 function to easier to flag
    if (this.unleashClient && !this.isStarted) {
      this.isStarted = true
      if (flags.canUseCacheProfileOnLoad) {
        this.unleashClient.on('update', () => {
          // whenever there is update, capture the current context
          // and store it to bootstrap
          const context = this.unleashClient.getContext()
          saveCachedUnleashContext(context)
        })
      }
      this.unleashClient.start()
    }
  }

  isFeatureEnabled(featureName) {
    if (!this.unleashClient) {
      return false
    }

    try {
      return this.unleashClient.isEnabled(featureName)
    } catch (error) {
      return false
    }
  }

  getVariant(featureName) {
    if (!this.unleashClient) {
      return { name: 'disabled', enabled: false }
    }

    try {
      return this.unleashClient.getVariant(featureName)
    } catch (error) {
      return { name: 'disabled', enabled: false }
    }
  }

  // Push the shared aha_unleash_session id into the client context so presenter
  // client-side flags bucket on the SAME anchor as the pricing page + the
  // server-side /entitlements path. Called once at bootstrap (before start()).
  setSessionId(sessionId) {
    if (!this.unleashClient || !sessionId) return
    try {
      this.unleashClient.setContextField('sessionId', sessionId)
    } catch (error) {
      sentry.captureException(error)
    }
  }
}
let params = {}
if (flags.canUseCacheProfileOnLoad) {
  const cachedUnleashContext = getCachedUnleashContext()
  params = cachedUnleashContext ? {
    experimental: {
      // make it 1 week
      // this only affects the initialFetch
      // we always trigger fetch again when updateContext
      // https://github.com/Unleash/unleash-js-sdk/blob/85e83a7f060d601bca1f6825e0f9501394159000/src/index.ts#L544
      togglesStorageTTL: 60 * 60 * 24 * 7 * 1000,
    },
    context: cachedUnleashContext,
  } : {}
}
export default new UnleashService(params)
